/**
 * CardTeamsThreadSection — live Teams Hub thread rendered inside the
 * universal Kanban CardDetailDrawer.
 *
 * Lifts the comment surface off the legacy `email_kanban` per-card
 * comments table and onto the existing Teams Hub
 * (organisation.TeamChannel + TeamMessage) so we get @mentions, SSE
 * notifications, reactions, replies, broadcast (@here / @all) and
 * read-receipts "for free" — instead of building a parallel comments
 * stack inside the kanban app.
 *
 * Wire-up:
 *   1. Mount → `api.fetchCardTeamsThread(card.id)` resolves (or
 *      creates) the channel for this card and seeds member access.
 *      Returns a small envelope with `channel_id` + a 5-message
 *      preview so the first paint is instant.
 *   2. Background → `api.listCardTeamMessages(channel_id)` runs every
 *      5 s for live-ish updates without forcing every tenant to wire
 *      the SSE stream. Tenants that DO wire SSE can pipe events into
 *      the same query key to bypass the poll.
 *   3. Compose → `api.sendCardTeamMessage(channel_id, payload)` POSTs
 *      to the canonical Teams REST surface (same one the dedicated
 *      `apps/teams` app uses); the server fans out notifications +
 *      mention notifications + SSE events.
 *
 * @mention autocomplete:
 *   * Driven entirely off the tenant-supplied `members` list (the
 *     same `KanbanMember[]` used by the assignee picker), so the
 *     directory shape stays consistent with the rest of the drawer.
 *   * Selecting a member inserts `@<name>` into the textarea AND
 *     pushes their user-id onto the `mentions: number[]` array sent
 *     with the POST. Server-side `_create_mention_notifications`
 *     creates a Notification + SSE for each id, exactly mirroring
 *     the dedicated Teams app.
 *   * `@here` and `@all` are recognised as broadcast tokens (no id
 *     to push); the backend's `_handle_broadcast_mentions` routes
 *     them to online / all members respectively.
 *
 * Operator backstory: 2026-05-11 — *"comments should go under the
 * Teams Module refer to Bravada's Team module ilift it as is and
 * make it function here. Don't forget to attach @tags and such."*
 * The Bravada "Team Module" being referenced is the same Teams Hub
 * (organisation.models_team_chat) that ships in the Quotech
 * unified backend; we lift it BY REFERENCE rather than by copy.
 */
import {
  Loader2,
  MessageSquare,
  Send,
} from 'lucide-react'
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import type { KanbanApi, KanbanMember } from '../api'
import type {
  CardTeamMessage,
  CardTeamMessageListResponse,
  CardTeamsThread,
} from '../api'
import type { Card } from '../types'
import { MentionTextarea } from './MentionTextarea'
import { CardTeamsThreadMessage } from './CardTeamsThreadMessage'

const POLL_MS = 5_000

/**
 * React Query retry policy for the teams-thread resolve.
 *
 * A 4xx (typically 404 — no Teams channel resolvable for this card under the
 * viewer's org scope, e.g. a cookie/bearer identity-drift session) is
 * PERMANENT for this card+session, so retrying it just strobes the network +
 * console. Retry a transient network/5xx ONCE; never retry a 4xx.
 * Errors thrown by the kanban `_request` helper carry a numeric `status`.
 * (Console-strobe fix 2026-06-22.)
 */
export function shouldRetryCardThread(failureCount: number, err: unknown): boolean {
  const status = (err as { status?: number } | null | undefined)?.status
  if (typeof status === 'number' && status >= 400 && status < 500) return false
  return failureCount < 1
}

interface CardTeamsThreadSectionProps {
  card: Card
  api: KanbanApi
  members?: KanbanMember[]
  currentUserId?: number | null
  /** BUG-2026-07-02-KANBAN-COMMENTS-INVISIBLE — legacy comments surface to
   *  render when the Teams Hub bridge is wired but can't resolve a channel
   *  for this card (4xx / no channel under the viewer's org scope). Without
   *  a fallback the composer + list never mount (they gate on `channelId`),
   *  so an operator could post a comment that never appears — or worse, have
   *  no composer at all. When supplied, the fallback (the legacy
   *  `CommentsSection`, which owns its own post→refetch→display round-trip)
   *  renders instead, GUARANTEEING a working comment path. */
  fallback?: ReactNode
}

export function CardTeamsThreadSection({
  card,
  api,
  members = [],
  currentUserId,
  fallback,
}: CardTeamsThreadSectionProps) {
  const qc = useQueryClient()
  const [draft, setDraft] = useState('')
  // ``mentionIds`` is kept in sync with ``draft`` by MentionTextarea
  // (re-derived from the text on each edit), so it always reflects the
  // teammates currently tagged in the composer.
  const [mentionIds, setMentionIds] = useState<number[]>([])
  const listRef = useRef<HTMLUListElement>(null)

  // BUG-2026-07-08-KANBAN-TEAMCHAT-CARD-LEAK — this section is a single
  // instance REUSED across cards (the drawer re-points at a new `card`
  // without remounting the subtree), so the composer's local `draft` +
  // `mentionIds` would otherwise carry the previous card's half-typed
  // (un-posted) message into the next card's Team Chat composer. Unlike the
  // "Card notes" composer, Team Chat has NO draft persistence by design, so
  // the correct behaviour is simply to CLEAR the composer whenever the card
  // identity changes. Reset on change ONLY (never mid-type for the same
  // card, which would clobber active typing). This is the same "adjust state
  // during render" pattern CommentsSection / KanbanCardPeekComments use for
  // the comment-draft stale-body guard. (In-progress edits of existing
  // messages live in CardTeamsThreadMessage, keyed by the unique message id,
  // so those rows unmount/remount on card switch and need no reset here.)
  const lastCardIdRef = useRef(card.id)
  if (lastCardIdRef.current !== card.id) {
    lastCardIdRef.current = card.id
    setDraft('')
    setMentionIds([])
  }

  const membersById = useMemo(() => {
    const m = new Map<number, KanbanMember>()
    for (const x of members) m.set(x.id, x)
    return m
  }, [members])

  // 1) Resolve channel id (and seed members on first call).
  const threadQ = useQuery<CardTeamsThread>({
    queryKey: ['kanban', 'card', card.id, 'teams-thread'],
    queryFn: () => api.fetchCardTeamsThread!(card.id),
    enabled: !!api.fetchCardTeamsThread,
    staleTime: 60_000,
    // A 4xx here (typically 404 = no Teams channel resolvable for this card
    // under the viewer's org scope — e.g. a cookie/bearer identity-drift
    // session) is PERMANENT for this card+session. React Query's defaults
    // (retry: 3 + refetchOnWindowFocus: true) turned that single 404 into a
    // network + console STROBE — it re-fired on every window refocus. Gate
    // both: never retry a 4xx, retry a transient network/5xx once, and don't
    // refetch on focus. (Console-strobe fix 2026-06-22.)
    retry: shouldRetryCardThread,
    refetchOnWindowFocus: false,
  })

  const channelId = threadQ.data?.channel_id

  // 2) Live message list — short poll until we wire SSE through.
  const messagesQ = useQuery<CardTeamMessageListResponse>({
    queryKey: ['kanban', 'card', card.id, 'teams-messages', channelId],
    queryFn: () => api.listCardTeamMessages!(channelId!, { limit: 50 }),
    enabled: !!channelId && !!api.listCardTeamMessages,
    refetchInterval: POLL_MS,
    refetchIntervalInBackground: false,
  })

  const sendMutation = useMutation({
    mutationFn: ({
      content,
      mentions,
    }: {
      content: string
      mentions: number[]
    }) =>
      api.sendCardTeamMessage!(channelId!, {
        content,
        mentions,
      }),
    onSuccess: () => {
      setDraft('')
      setMentionIds([])
      // Refetch the live message list — this is a RAW `fetch`
      // (`listCardTeamMessages`), so a transient 401 can only surface as
      // a caught query error, never the tab-wide auth-lost bounce.
      qc.invalidateQueries({
        queryKey: ['kanban', 'card', card.id, 'teams-messages', channelId],
      })
      // BUG-2026-06-29 — do NOT invalidate the `teams-thread` query here.
      // Re-resolving the thread goes through `_request`/quotechFetch, whose
      // 401 path fires QUOTECH_AUTH_LOST_EVENT → the host bounces the tab to
      // `/login?reason=invalid`. On a drifted session (e.g. multiple tabs on
      // different identities) that turned "post a comment" into "the page
      // randomly refreshed". Posting must never re-resolve auth. We only
      // needed the thread refetch to keep the count badge current — bump it
      // locally in the cache instead (zero network, zero bounce risk).
      qc.setQueryData<CardTeamsThread>(
        ['kanban', 'card', card.id, 'teams-thread'],
        (prev) =>
          prev && typeof prev.message_count === 'number'
            ? { ...prev, message_count: prev.message_count + 1 }
            : prev,
      )
    },
  })

  // Delete one's own message. Sender-only is enforced server-side
  // (403 for non-senders); we additionally only render the control on
  // `m.is_own` so it never appears for messages the viewer can't remove.
  const deleteMutation = useMutation({
    mutationFn: (messageId: string) =>
      api.deleteCardTeamMessage!(channelId!, messageId),
    onSuccess: (_void, messageId) => {
      // Optimistically drop the row from the cached list so it vanishes
      // immediately, then reconcile with the server on the next poll.
      qc.setQueryData<CardTeamMessageListResponse>(
        ['kanban', 'card', card.id, 'teams-messages', channelId],
        (prev) =>
          prev
            ? { ...prev, messages: prev.messages.filter((m) => m.id !== messageId) }
            : prev,
      )
      qc.setQueryData<CardTeamsThread>(
        ['kanban', 'card', card.id, 'teams-thread'],
        (prev) =>
          prev && typeof prev.message_count === 'number'
            ? { ...prev, message_count: Math.max(0, prev.message_count - 1) }
            : prev,
      )
      qc.invalidateQueries({
        queryKey: ['kanban', 'card', card.id, 'teams-messages', channelId],
      })
    },
  })

  // Edit one's own message. Sender-only + 15-min window enforced server-side
  // (403 / 400); we additionally only render the pencil on `m.is_own` within
  // the window. The BE returns just `{id, content, is_edited}`, so we patch
  // the cached row in place and let the next poll reconcile the rest.
  const editMutation = useMutation({
    mutationFn: ({
      messageId,
      content,
    }: {
      messageId: string
      content: string
    }) => api.editCardTeamMessage!(channelId!, messageId, content),
    onSuccess: (res) => {
      qc.setQueryData<CardTeamMessageListResponse>(
        ['kanban', 'card', card.id, 'teams-messages', channelId],
        (prev) =>
          prev
            ? {
                ...prev,
                messages: prev.messages.map((msg) =>
                  msg.id === res.id
                    ? { ...msg, content: res.content, is_edited: res.is_edited }
                    : msg,
                ),
              }
            : prev,
      )
      qc.invalidateQueries({
        queryKey: ['kanban', 'card', card.id, 'teams-messages', channelId],
      })
    },
  })

  const editMessage = useCallback(
    async (messageId: string, content: string) => {
      await editMutation.mutateAsync({ messageId, content })
    },
    [editMutation],
  )

  const send = useCallback(() => {
    const text = draft.trim()
    if (!text || !channelId) return
    // ``mentionIds`` is already accurate (MentionTextarea re-derives it
    // from the text on every edit), so no prune pass is needed.
    sendMutation.mutate({ content: text, mentions: mentionIds })
  }, [draft, mentionIds, channelId, sendMutation])

  // Auto-scroll to the newest message (top) when the list grows —
  // BUG-2026-07-16-KANBAN-TEAM-CHAT-OLDEST-FIRST: display is newest-first
  // like card notes, so the viewport anchors at scrollTop 0.
  useEffect(() => {
    if (!listRef.current) return
    listRef.current.scrollTop = 0
  }, [messagesQ.data?.messages.length])

  // BUG-2026-07-16-KANBAN-TEAM-CHAT-OLDEST-FIRST — Teams API returns
  // chronological (oldest first); card notes reverse for newest-on-top.
  // Hoisted above guard returns (hooks-order gate).
  const wireMessages = messagesQ.data?.messages
  const displayMessages = useMemo(
    () => (wireMessages ? wireMessages.slice().reverse() : []),
    [wireMessages],
  )

  if (!api.fetchCardTeamsThread) {
    // Tenant didn't wire the bridge — render the legacy comments fallback
    // when the parent supplied one (so the composer + list still work),
    // else nothing rather than half-broken UI.
    return <>{fallback ?? null}</>
  }

  // BUG-2026-07-02-KANBAN-COMMENTS-INVISIBLE — the bridge is wired but has
  // settled WITHOUT a usable channel (4xx / no channel resolvable for this
  // card under the viewer's org scope, or a resolved thread that carries no
  // channel_id). The composer + message list below gate on `channelId`, so in
  // this state a lone "Team chat · 0 — no messages" header would be exactly
  // the misleading empty surface this bug is about. When the parent supplied a
  // legacy `fallback`, render it (guarantees a working composer). Otherwise
  // render NOTHING and let the drawer's always-on "Card notes" section be the
  // sole comment surface. Still loading → keep showing the connecting state
  // (don't flash while the channel is resolving).
  if (!threadQ.isLoading && !channelId) {
    return <>{fallback ?? null}</>
  }

  return (
    <section data-kanban-focus="team-chat">
      <h3 className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
        <MessageSquare className="h-3 w-3" /> Team chat
        {threadQ.data?.message_count != null && (
          <span className="ml-1 rounded-full bg-neutral-100 px-1.5 py-px text-[10px] font-normal text-neutral-500 dark:bg-neutral-800">
            {threadQ.data.message_count}
          </span>
        )}
      </h3>

      {threadQ.isLoading && (
        <p className="text-xs text-neutral-400">Connecting to Teams Hub…</p>
      )}
      {threadQ.error && (
        <p className="text-xs text-red-500">
          Couldn't reach Teams Hub: {(threadQ.error as Error).message}
        </p>
      )}

      {channelId && (
        <>
          <ul
            ref={listRef}
            className="mb-2 max-h-72 space-y-1.5 overflow-auto pr-1"
          >
            {messagesQ.isLoading && displayMessages.length === 0 && (
              <li className="text-xs text-neutral-400">Loading…</li>
            )}
            {displayMessages.length === 0 && !messagesQ.isLoading && (
              <li className="text-xs text-neutral-400">
                No messages yet — start the chat.
              </li>
            )}
            {displayMessages.map((m) => (
              <CardTeamsThreadMessage
                key={m.id}
                message={m}
                membersById={membersById}
                currentUserId={currentUserId}
                canDelete={!!api.deleteCardTeamMessage}
                deletePending={deleteMutation.isPending}
                onDelete={(id) => deleteMutation.mutate(id)}
                canEdit={!!api.editCardTeamMessage}
                onEdit={editMessage}
              />
            ))}
          </ul>

          <div className="relative">
            <MentionTextarea
              value={draft}
              onChange={(v, ids) => {
                setDraft(v)
                setMentionIds(ids)
              }}
              members={members}
              onSubmit={send}
              placeholder="Write a comment…  @ to mention,  Cmd+Enter to send"
              rows={2}
              ariaLabel="Write a comment"
              className="w-full resize-none rounded-md border border-neutral-200 bg-white px-2 py-1.5 text-xs focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500/30 dark:border-neutral-800 dark:bg-neutral-900"
            />
            <div className="mt-1 flex items-center justify-between">
              <p className="text-[10px] text-neutral-400">
                Posts to Teams Hub · @ tags trigger notifications
                {sendMutation.isPending && ' · Sending…'}
                {deleteMutation.isPending && ' · Deleting…'}
                {sendMutation.error && (
                  <span className="ml-1 text-red-500">
                    · {(sendMutation.error as Error).message}
                  </span>
                )}
                {deleteMutation.error && (
                  <span className="ml-1 text-red-500">
                    · {(deleteMutation.error as Error).message}
                  </span>
                )}
              </p>
              <button
                type="button"
                onClick={send}
                disabled={!draft.trim() || sendMutation.isPending}
                className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {sendMutation.isPending ? (
                  <Loader2 className="h-2.5 w-2.5 animate-spin" />
                ) : (
                  <Send className="h-3 w-3" />
                )}
                Post
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  )
}

export default CardTeamsThreadSection
